import {
  Component,
  computed,
  inject,
  input,
  output,
  signal,
  ChangeDetectionStrategy
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { AuthService, SHELL_NAV_ROUTES, SIDEBAR_ROUTE_ICONS, TabService } from '@core';

export interface PrimaryNavItem {
  id: string;
  label: string;
  icon: string;
  route: string;
}

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './sidebar.component.html'
})
export class SidebarComponent {
  private readonly router = inject(Router);
  private readonly tabService = inject(TabService);
  private readonly authService = inject(AuthService);

  readonly isMobile = input<boolean>(false);
  readonly collapsed = input<boolean>(false);
  readonly closeDrawer = output<void>();
  readonly toggleCollapse = output<void>();

  readonly primaryItems: PrimaryNavItem[] = SHELL_NAV_ROUTES.map((item) => ({
    id: item.id,
    label: item.label,
    icon: item.icon,
    route: item.route!
  }));

  readonly activePrimary = signal<string>('dashboard');
  readonly isCollapsed = computed(() => !this.isMobile() && this.collapsed());

  constructor() {
    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe(() => {
        const url = this.router.url.split('?')[0];
        if (url.startsWith('/tenants')) this.activePrimary.set('tenants');
        else if (url.startsWith('/document-endpoints'))
          this.activePrimary.set('document-endpoints');
        else if (url.startsWith('/dashboard') || url === '/')
          this.activePrimary.set('dashboard');
        else this.activePrimary.set('dashboard');
      });
  }

  selectPrimary(item: PrimaryNavItem): void {
    this.activePrimary.set(item.id);
    this.tabService.openTab({ id: item.id, title: item.label, route: item.route });
    this.router.navigate([item.route]);
    if (this.isMobile()) this.closeDrawer.emit();
  }

  logout(): void {
    this.authService.logout().subscribe({
      next: () => {
        this.authService.removeTokens();
        this.router.navigate(['/auth/login']);
      },
      error: () => {
        this.authService.removeTokens();
        this.router.navigate(['/auth/login']);
      }
    });
  }

  userInitials(): string {
    return 'AD';
  }

  userDisplayName(): string {
    return 'Admin';
  }

  readonly logoIcon = SIDEBAR_ROUTE_ICONS.dashboard;
}
